/**
 * Builds the `X-Rx-Change-Context` header value used by mutating endpoints
 * (today: meal/meal-item DELETE) for change tracking.
 *
 * Mirrors the v1 swagger `QA2ChangeContext` shape:
 *   { review_id?, review_time, change_reason, addons_change_reason? }
 */
export type ChangeContext = {
  reason: string;
  reviewId?: string | number;
  addonsReason?: string;
  /** Defaults to `new Date().toISOString()` if omitted. */
  reviewTime?: string;
};

export const buildChangeContext = (ctx: ChangeContext): string =>
  JSON.stringify({
    // Emit `null` (not undefined) when there's no review. JSON.stringify drops
    // undefined-valued keys, but the server reads `change_context["review_id"]`
    // with a hard subscript — a missing key 500s. `null` keeps the key present,
    // and the server treats it as "no review" (the field is optional there).
    review_id: ctx.reviewId ?? null,
    review_time: ctx.reviewTime ?? new Date().toISOString(),
    change_reason: ctx.reason,
    addons_change_reason: ctx.addonsReason ?? null,
  });

/**
 * Convenience: returns an axios-compatible `headers` object so call sites can spread it
 * into a request config:
 *
 *   apiClient.delete(url, { ...withChangeContext({ reason: 'fix typo' }) })
 */
export const withChangeContext = (ctx: ChangeContext) => ({
  headers: { 'X-Rx-Change-Context': buildChangeContext(ctx) },
});
